import { DishReviews } from './DishReviews'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { styled } from '@linaria/react'
import { MinimalBadge, MinimalButton, MinimalCard, MinimalEmptyState } from '@ovasabi/ui-minimal'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import {
  DishRow,
  Money,
  PageHeader,
  Screen,
  SectionHeading,
  Stepper,
} from '../../components/chow'
import { BikeIcon, ClockIcon, PlateIcon, StarIcon } from '../../components/icons'
import { ChowThumb } from './ChowThumb'
import { KitchenBadge } from '../../components/chow/KitchenBadge'
import { addDishToCart } from './cart'
import { useAuth } from './auth/AuthContext'
import { recordPendingAction } from './auth/pendingAction'
import { useFavourites } from './favourites'
import {
  dishCardEta,
  locationLine,
  useUiDish,
  useUiDishes,
  useVendorLocations,
} from './projections'
import { PlanDishModal } from './PlanDishModal'
import { useBackNav, useChooseChow } from './ChooseChowContext'
import { dayLabel, WEEKDAYS, type Weekday } from '../../lib/vendorSchedule'
import { canonicalSlug } from '../../lib/slug'

/**
 * Individual dish page — the expandable detail the dish rows link through to.
 * Sections mirror what an eater needs to decide: hero (photo/price/chef),
 * quick facts (cook time, category, days offered), main ingredients, allergen
 * categories (with a personal warning when they intersect the signed-in
 * profile's allergies), a reviews seam, and the chef's full menu at the
 * bottom for lateral discovery.
 */

const Style = {
  /* Full-bleed hero card: a big appetising cover, then a padded info block —
     the reference dish-detail treatment, and consistent with the dish cards. */
  HeroCard: styled.div`
    background: ${minimalVars.color.bgSurface};
    border: 1px solid ${minimalVars.color.borderSubtle};
    border-radius: ${minimalVars.radius.xl};
    overflow: hidden;
    box-shadow: ${minimalVars.shadow.subtle};
  `,
  Cover: styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: linear-gradient(
      140deg,
      ${minimalVars.color.brandSoft},
      ${minimalVars.color.bgSurfaceAlt}
    );
    overflow: hidden;
    &[data-sold-out] {
      filter: grayscale(0.5);
      opacity: 0.8;
    }

    &::after {
      content: '';
      position: absolute;
      inset: auto 0 0 0;
      height: 42%;
      background: linear-gradient(to top, rgba(20, 10, 5, 0.32), transparent);
      pointer-events: none;
    }
  `,
  CoverFill: styled.div`
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    & > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    & > svg {
      width: 100%;
      height: 100%;
    }
    & > span {
      width: 100%;
      height: 100%;
      display: grid;
      place-items: center;
    }
  `,
  RatingChip: styled.span`
    position: absolute;
    left: 12px;
    bottom: 12px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 5px 11px;
    border-radius: ${minimalVars.radius.pill};
    background: ${minimalVars.color.success};
    color: #fff;
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    font-variant-numeric: tabular-nums;
    box-shadow: 0 2px 8px -2px rgba(20, 10, 5, 0.4);
    & > svg {
      color: #fff;
    }
  `,
  HeroInfo: styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${minimalVars.space.sm};
    padding: ${minimalVars.space.sm};
  `,
  HeroBody: styled.div`
    flex: 1;
    /* Without this the chef name can widen the flex item and push the
       favourite control off the card on narrow phones. */
    min-width: 0;
    display: grid;
    gap: ${minimalVars.space['2xs']};
    justify-items: start;
  `,
  HeroFacts: styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 12px;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    font-variant-numeric: tabular-nums;
    margin-top: 2px;

    & span {
      display: inline-flex;
      align-items: center;
      gap: 5px;
    }
    & svg {
      color: ${minimalVars.color.textTertiary};
    }
  `,
  /* Truncates rather than wrapping — a long chef name was pushing the hero
     onto a second line and unbalancing the card. */
  ChefLink: styled.button`
    max-width: 100%;
    background: none;
    border: 0;
    padding: 0;
    text-align: left;
    cursor: pointer;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        color: ${minimalVars.color.brand};
      }
    }
  `,
  Description: styled.p`
    margin: 0;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.bodySize};
    line-height: ${minimalVars.typography.lineHeightBody};
  `,
  /**
   * Dish facts as a definition list, not bordered tiles. The previous tiles
   * read as tappable buttons, which they never were — flat label/value rows
   * separated by hairlines carry the same information without competing with
   * the page's real actions.
   */
  Facts: styled.dl`
    display: grid;
    gap: 0;
    margin: 0;
  `,
  Fact: styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: ${minimalVars.space.sm};
    padding: ${minimalVars.space.xs} 0;
    border-bottom: 1px solid ${minimalVars.color.borderSubtle};

    &:last-of-type {
      border-bottom: 0;
    }
  `,
  FactLabel: styled.dt`
    flex-shrink: 0;
    font-size: ${minimalVars.typography.captionSize};
    color: ${minimalVars.color.textTertiary};
  `,
  FactValue: styled.dd`
    margin: 0;
    min-width: 0;
    text-align: right;
    font-size: ${minimalVars.typography.captionSize};
    color: ${minimalVars.color.textPrimary};
  `,
  /** Icon-only favourite toggle; 44px pointer target, icon-scale glyph. */
  Star: styled.button<{ $on: boolean }>`
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    border-radius: ${minimalVars.radius.pill};
    color: ${({ $on }) => ($on ? minimalVars.color.brand : minimalVars.color.textTertiary)};
    transition: color 160ms ease;

    &:focus-visible {
      outline: 2px solid ${minimalVars.color.brand};
      outline-offset: 2px;
    }
  `,
  Chips: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${minimalVars.space['2xs']};
  `,
  /* Tone comes from theme tokens — `danger` for an allergen the eater avoids,
     neutral surface otherwise. No raw hex per the visual identity contract. */
  Chip: styled.span<{ $warn?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: ${minimalVars.space['2xs']};
    padding: 3px 8px;
    border-radius: 10px;
    font-size: ${minimalVars.typography.captionSize};
    color: ${({ $warn }) => ($warn ? minimalVars.color.danger : minimalVars.color.textSecondary)};
    background: ${minimalVars.color.bgSurfaceAlt};
    border: 1px solid
      ${({ $warn }) => ($warn ? minimalVars.color.danger : minimalVars.color.borderSubtle)};
  `,
  AddBar: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.sm};
    flex-wrap: wrap;
  `,
  AddControls: styled.div`
    display: flex;
    align-items: center;
    gap: ${minimalVars.space.xs};
  `,
  /* The planning action is deliberately below the buy row and full width
     rather than beside it. "Add to cart" stays the one primary action on this
     page; a second button of equal weight in the same row makes the customer
     choose between two things when they came here to do one. */
  PlanRow: styled.div`
    display: grid;
    gap: 4px;
    margin-top: ${minimalVars.space.xs};
    padding-top: ${minimalVars.space.xs};
    border-top: 1px solid ${minimalVars.color.borderSubtle};
    justify-items: start;
  `,
  PlanHint: styled.span`
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
  `,
  List: styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${minimalVars.space.sm};

    /* 600px matches no named stop; it sits under the page stop. */
    @media (min-width: 600px) {
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    }
  `,
  Section: styled.div`
    display: grid;
    gap: ${minimalVars.space.xs};
  `,
}

const formatDays = (days: readonly number[]): string => {
  if (days.length === 0 || days.length === 7) return 'Every day'
  return days
    .filter((d): d is Weekday => (WEEKDAYS as readonly number[]).includes(d))
    .map((d) => dayLabel(d))
    .join(' · ')
}

export const DishDetailPage = () => {
  const { dishId = '' } = useParams()
  const navigate = useNavigate()
  const back = useBackNav()
  const { profileId, isChef } = useChooseChow()
  const { status } = useAuth()
  const dish = useUiDish(dishId)
  const { records: allDishes } = useUiDishes()
  const favourites = useFavourites(profileId)
  const chefLocations = useVendorLocations(dish?.chefId ?? '')
  const [qty, setQty] = useState(1)
  const [added, setAdded] = useState(false)
  const [planning, setPlanning] = useState(false)

  const chefMenu = useMemo(
    () => (dish ? allDishes.filter((d) => d.chefId === dish.chefId && d.id !== dish.id) : []),
    [allDishes, dish],
  )

  if (!dish) {
    return (
      <Screen header={<PageHeader title="Dish not found" accentDot />}>
        <MinimalEmptyState
          icon={<PlateIcon />}
          title="This dish is no longer available"
          description="It may have been removed by the chef, or the link is out of date."
          action={
            <MinimalButton variant="secondary" onClick={() => navigate('/dishes')}>
              Browse dishes
            </MinimalButton>
          }
        />
      </Screen>
    )
  }

  const soldOut = dish.status === 'sold_out' || dish.stock === 0

  // Planning is a customer action on a dish they can actually be sold: a chef
  // looking at their own menu is not planning their own meals here, and a
  // sold-out dish cannot be held for a day.
  const canPlan = !isChef && !soldOut

  const add = () => {
    if (status !== 'authenticated' || !profileId) {
      recordPendingAction({
        type: 'add_to_cart',
        dish: {
          id: dish.id,
          name: dish.name,
          price: dish.price,
          chefId: dish.chefId,
          chef: dish.chef,
          category: dish.category,
          image: dish.image,
          glyph: dish.glyph,
        },
        quantity: qty,
        returnTo: `/dish/${dish.id}`,
        timestamp: Date.now(),
      })
      navigate('/login?returnTo=' + encodeURIComponent(`/dish/${dish.id}`))
      return
    }
    addDishToCart(dish, qty, profileId)
    setAdded(true)
    setQty(1)
    window.setTimeout(() => setAdded(false), 2500)
  }

  const handlePlanClick = () => {
    if (status !== 'authenticated' || !profileId) {
      recordPendingAction({
        type: 'plan_meal',
        dish: {
          id: dish.id,
          name: dish.name,
          price: dish.price,
          chefId: dish.chefId,
          chef: dish.chef,
          category: dish.category,
          image: dish.image,
          glyph: dish.glyph,
        },
        quantity: qty,
        returnTo: `/dish/${dish.id}`,
        timestamp: Date.now(),
      })
      navigate('/login?returnTo=' + encodeURIComponent(`/dish/${dish.id}`))
      return
    }
    setPlanning(true)
  }

  return (
    <Screen
      header={
        /* Back belongs in the header's own left-hand slot; the top-right
           corner is the cart's, and a button there collided with it. */
        <PageHeader
          title={dish.name}
          accentDot
          subtitle={`by ${dish.chef}`}
          onBack={back}
        />
      }
    >
      {/* Hero: a big cover, then price, chef link and delivery facts. The dish
          name and chef are already the page header, so not repeated here. */}
      <Style.HeroCard>
        <Style.Cover data-sold-out={soldOut || undefined}>
          <Style.CoverFill>
            <ChowThumb src={dish.image} emoji={dish.glyph} alt={dish.name} category={dish.category} name={dish.name} />
          </Style.CoverFill>
          {!soldOut && dish.rating !== undefined && (
            <Style.RatingChip>
              <StarIcon size="sm" fill="currentColor" />
              {dish.rating.toFixed(1)}
            </Style.RatingChip>
          )}
        </Style.Cover>
        <Style.HeroInfo>
          <Style.HeroBody>
            <Money amount={dish.price} tone="brand" size="lg" />
            {/* The kitchen's verification badge, above the fold on every dish
                page (verification spec section 7). */}
            <KitchenBadge tier={dish.chefBadge} />
            <Style.ChefLink
              onClick={() => navigate(`/kitchen/${canonicalSlug(dish.chef, dish.chefId || '')}`)}
            >
              See more from {dish.chef}
            </Style.ChefLink>
            <Style.HeroFacts>
              {dish.cookTimeMinutes > 0 && <span>
                <ClockIcon size="sm" />
                {dishCardEta(dish.cookTimeMinutes)}
              </span>}
              <span>
                <BikeIcon size="sm" />
                Kitchen pickup
              </span>
            </Style.HeroFacts>
            {soldOut && <MinimalBadge tone="danger" size="sm">Sold out</MinimalBadge>}
          </Style.HeroBody>
          <Style.Star
            type="button"
            $on={favourites.isFavourite(dish.id)}
            aria-pressed={favourites.isFavourite(dish.id)}
            aria-label={
              favourites.isFavourite(dish.id) ? 'Remove from favourites' : 'Save to favourites'
            }
            onClick={() => favourites.toggle(dish.id)}
          >
            <StarIcon
              size="md"
              fill={favourites.isFavourite(dish.id) ? 'currentColor' : 'none'}
            />
          </Style.Star>
        </Style.HeroInfo>
        {dish.description && (
          <Style.Description style={{ padding: '0 16px 16px' }}>{dish.description}</Style.Description>
        )}
      </Style.HeroCard>


      {/* One details card: label/value facts, then the chef's own notes. Three
          separate display headings for a few lines of text was all heading and
          no content. */}
      <MinimalCard padding="md" enter={false}>
        <Style.Facts>
          <Style.Fact>
            <Style.FactLabel>Category</Style.FactLabel>
            <Style.FactValue style={{ textTransform: 'capitalize' }}>
              {dish.category}
            </Style.FactValue>
          </Style.Fact>
          <Style.Fact>
            <Style.FactLabel>Cook time</Style.FactLabel>
            <Style.FactValue>
              {dish.cookTimeMinutes > 0 ? `${dish.cookTimeMinutes} min` : 'Not specified'}
            </Style.FactValue>
          </Style.Fact>
          <Style.Fact>
            <Style.FactLabel>Offered</Style.FactLabel>
            <Style.FactValue>{formatDays(dish.availableDays)}</Style.FactValue>
          </Style.Fact>
          {chefLocations.length > 0 && (
            <Style.Fact>
              <Style.FactLabel>Cooked in</Style.FactLabel>
              <Style.FactValue>
                {locationLine(chefLocations[0])}
                {chefLocations.length > 1 ? ` +${chefLocations.length - 1}` : ''}
              </Style.FactValue>
            </Style.Fact>
          )}
          <Style.Fact>
            <Style.FactLabel>Ingredients</Style.FactLabel>
            <Style.FactValue>
              {dish.ingredients.length === 0 ? 'Not listed' : dish.ingredients.join(', ')}
            </Style.FactValue>
          </Style.Fact>
        </Style.Facts>

      </MinimalCard>

      {!isChef && (
        <MinimalCard padding="md" enter={false}>
          {/* One primary action. Quantity uses the shared Stepper so it reads
              identically to every dish row instead of three same-weight
              buttons competing with each other. */}
          <Style.AddBar>
            <Money amount={dish.price * qty} tone="brand" size="md" />
            <Style.AddControls>
              <Stepper value={qty} onChange={setQty} min={1} disabled={soldOut} />
              <MinimalButton onClick={add} disabled={soldOut || added}>
                {added ? 'Added' : soldOut ? 'Sold out' : 'Add to cart'}
              </MinimalButton>
            </Style.AddControls>
          </Style.AddBar>
          {canPlan && (
            <Style.PlanRow>
              <MinimalButton
                size="sm"
                variant="ghost"
                onClick={handlePlanClick}
              >
                Plan for a day
              </MinimalButton>
              <Style.PlanHint>
                Hold it for a day this fortnight instead of ordering now — it joins your meal plan.
              </Style.PlanHint>
            </Style.PlanRow>
          )}
        </MinimalCard>
      )}

      {canPlan && (
        <PlanDishModal
          open={planning}
          dish={dish}
          initialQuantity={qty}
          onClose={() => setPlanning(false)}
        />
      )}

      <Style.Section>
        <SectionHeading title={`More from ${dish.chef}`} />
        {chefMenu.length === 0 ? (
          <Style.Description>This is the only dish listed by {dish.chef} right now.</Style.Description>
        ) : (
          <Style.List>
            {chefMenu.map((d) => (
              <DishRow
                key={d.id}
                name={d.name}
                chef={d.chef}
                chefGlyph={d.chefGlyph}
                glyph={<ChowThumb src={d.image} emoji={d.glyph} alt={d.name} />}
                price={d.price}
                qty={1}
                onQty={() => undefined}
                to={`/dishes/${encodeURIComponent(d.id)}`}
                onAdd={!isChef ? () => addDishToCart(d, 1, profileId) : undefined}
                stock={d.stock}
                status={d.status}
                rating={d.rating}
                etaLabel={dishCardEta(d.cookTimeMinutes)}
              />
            ))}
          </Style.List>
        )}
      </Style.Section>
      <DishReviews key={dish.id} dishId={dish.id} profileId={profileId} name={dish.name} />
    </Screen>
  )
}
